import { useEffect, useRef, useState } from "react";
import {
  DiseaseCategoryType,
  Feature,
  PatientSuggestionsClustered,
  PatientSuggestionsDebugSheet,
  PatientSuggestionsPerDiseaseMetrics,
  PatientSuggestionsSuggestions,
  QualifiedFeature,
  QualifiedFeatureFragment,
  SortType,
} from "fe-common/graphql";
import { useCalculateSuggestionReasoning } from "../../../util/FindingStrengthCalculator";
import { patientCaseByPresentationType } from "../updateDDToState";
import { useDaoContext } from "fe-web-components/diseaseview/DaoContext";
import { pathOptions } from "./consts";
import { DataInputPanel } from "../../dataInputTypes";
import { useDataInputPanels } from "../useDataInputPanels";
import { v4 as uuid } from "uuid";
import { useGetDiseasesSubTypesForPlan } from "./useGetDiseasesSubTypesForPlan";
import { DiseaseCluster } from "fe-common/hooks/sortFeaturesWithCrs";
import { getFeatureFlags } from "fe-common/featureFlags/getFeatureFlags";
import { PatientCase } from "../../../context/diagnosticAgent";

export type SuggestionDiseaseStrength = {
  disease: QualifiedFeatureFragment;
  strength: number;
};

export type Suggestion = {
  category?: DiseaseCategoryType;
  finding: QualifiedFeatureFragment;
  inPatient?: boolean;
  inPatientId?: string;
  causedByDisease?: (PatientSuggestionsPerDiseaseMetrics &
    SuggestionDiseaseStrength)[];
  panelIds?: DataInputPanel[];
  prob?: number;
  findingLeak?: number;
};

export type ClusteredSuggestions = {
  title?: Feature;
  category?: DiseaseCategoryType;
  suggestions: Suggestion[];
};

export interface UseSuggestions {
  findingSuggestions: Suggestion[];
  suggestionDebug: PatientSuggestionsDebugSheet;
  planIsLoading: boolean;
  clusteredSuggestions: ClusteredSuggestions[];
}

interface UseSuggestionsProps {
  patientCase?: string | PatientCase;
  planNeeded?: boolean;
  diseases?: DiseaseCluster[];
  onDone?: () => void;
  diseasesToIncludeInPlan?: QualifiedFeature[];
  initialSuggestionState?: UseSuggestions;
  categories?: DiseaseCategoryType[];
  includeDiseaseMetrics?: boolean;
  sortType?: SortType;
}

const DEFAULT_LIMIT = 25;
const LIMIT_PER_CATEGORY = {
  [DiseaseCategoryType.OutcomeSigns]: 10,
  [DiseaseCategoryType.Imaging]: 10,
};
const DEFAULT_CATEGORIES = [
  DiseaseCategoryType.OutcomeTest,
  DiseaseCategoryType.OutcomeSigns,
  DiseaseCategoryType.Imaging,
];

export const useSuggestions = ({
  patientCase,
  planNeeded = true,
  diseases,
  onDone,
  diseasesToIncludeInPlan,
  initialSuggestionState,
  categories = DEFAULT_CATEGORIES,
  includeDiseaseMetrics = true,
  sortType,
}: UseSuggestionsProps = {}): UseSuggestions => {
  const initPlanState = initialSuggestionState || {
    findingSuggestions: [],
    clusteredSuggestions: [],
    suggestionDebug: null,
    planIsLoading: false,
  };
  const [plan, setPlan] = useState<PatientSuggestionsSuggestions[]>([]);
  const [clustered, setClusteredSuggestion] = useState<
    PatientSuggestionsClustered[]
  >([]);

  const [planState, setPlanState] = useState<UseSuggestions>(initPlanState);

  const dao = useDaoContext();

  const { panels } = useDataInputPanels();

  let currentSessionId = useRef<string>();

  const debugSheet = getFeatureFlags("debugSheet") as boolean;

  // The solver returns the index of the disease from this list
  const {
    diseasesForPlan: diseasesSubTypesForPlan,
    aliasing,
  } = useGetDiseasesSubTypesForPlan({
    diseases,
    diseasesToIncludeInPlan,
    sortType,
  });

  useEffect(() => {
    if (!planNeeded) return;

    if (!diseasesSubTypesForPlan?.length) {
      setPlan([]);
      return;
    }

    let grouped = {};
    let caseId = null;

    if (typeof patientCase !== "string") {
      grouped = patientCaseByPresentationType(patientCase);
    } else {
      caseId = patientCase;
    }

    // Go get the plan
    setPlanState((prevState) => ({
      ...prevState,
      planIsLoading: true,
    }));

    currentSessionId.current = uuid();
    let sessionId = currentSessionId.current;
    (async () => {
      const plan = await dao.getPlan(
        grouped,
        diseasesSubTypesForPlan,
        pathOptions,
        debugSheet,
        categories,
        includeDiseaseMetrics,
        DEFAULT_LIMIT,
        LIMIT_PER_CATEGORY,
        aliasing,
        caseId,
      );
      if (currentSessionId.current === sessionId) {
        // Update suggestions in plan
        setPlan(
          plan.suggestions.filter(
            (suggestion) => suggestion.suggestion.feature,
          ),
        );
        setClusteredSuggestion(plan.clustered);
        setPlanState((prevState) => ({
          ...prevState,
          suggestionDebug: plan.debugSheet,
          planIsLoading: false,
        }));

        onDone && onDone();
      }
    })();
    // PatientCase is missing on purpose, we want a new plan only if the diseasesSubTypesForPlan changed
    // eslint-disable-next-line
  }, [dao, diseasesSubTypesForPlan, categories]);

  const calculatePerDiseaseReasoning = useCalculateSuggestionReasoning(
    diseases,
    diseasesSubTypesForPlan,
    plan,
  );

  useEffect(() => {
    const panelsArray = panels ? [...panels.values()] : [];
    const fSuggestions = plan?.map((suggestion) => ({
      inPatient: suggestion.inPatient,
      category: suggestion.category,
      finding: suggestion.suggestion,
      inPatientId: suggestion.inPatientId,

      findingLeak: suggestion.findingLeak,
      prob: suggestion.findingProbability,
      causedByDisease: calculatePerDiseaseReasoning(
        suggestion.perDiseaseMetrics || [],
      ),

      panelIds: panelsArray.filter((panel) =>
        panel.findings
          .filter((f) => f?.feature?.id)
          .some((f) => suggestion.suggestion?.feature?.id === f.feature.id),
      ),
    }));

    const clusteredSuggestions = clustered?.map((c) => ({
      category: c.category,
      title: c.title,
      suggestions: c.items.map((s) => ({
        causedByDisease: calculatePerDiseaseReasoning(s.perDiseaseMetrics),
        finding: s.suggestion,
      })),
    }));

    if (fSuggestions && fSuggestions.length) {
      setPlanState((prevState) => ({
        ...prevState,
        findingSuggestions: fSuggestions,
        clusteredSuggestions,
      }));
    }
  }, [panels, plan, calculatePerDiseaseReasoning, setPlanState, clustered]);

  return { ...planState };
};
